import React, { useRef, useEffect } from 'react';
import { getPlotPalette, onThemeChange } from '../theme/themeColors';

function PerformanceMetricsPlot() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // DPI-aware sizing
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        const w = rect.width;
        const h = rect.height;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = w + 'px';
        canvas.style.height = h + 'px';
        const g = canvas.getContext('2d');
        g.scale(dpr, dpr);

        const pad = { top: 28, right: 24, bottom: 32, left: 46 };
        const plotW = w - pad.left - pad.right;
        const plotH = h - pad.top - pad.bottom;
        const pts = 100;
        let animFrame;
        let p = getPlotPalette();
        const offThemeChange = onThemeChange(() => {
            p = getPlotPalette();
        });

        // Precompute data points
        const vus = [];       // Virtual Users (0-50)
        const rps = [];       // Requests/sec (0-250)
        const p95 = [];       // p95 latency ms (0-120)
        for (let i = 0; i < pts; i++) {
            const frac = i / (pts - 1);
            // Ramp up VUs over first 30%, hold, then ramp down
            if (frac < 0.3) vus.push(Math.round(50 * (frac / 0.3)));
            else if (frac < 0.8) vus.push(50 + Math.round(2 * Math.sin(frac * 4)));
            else vus.push(Math.round(50 * (1 - (frac - 0.8) / 0.2)));

            // RPS roughly tracks VUs but with noise and slight lag
            const baseRps = 180 * (vus[i] / 50) + 20;
            const rpsVal = baseRps + 15 * Math.sin(i * 0.4) + 8 * Math.sin(i * 0.15);
            rps.push(Math.max(0, rpsVal));

            // p95 latency goes up with load
            const baseLat = 15 + 55 * (vus[i] / 50);
            const latVal = baseLat + 10 * Math.sin(i * 0.25) + 6 * Math.sin(i * 0.55);
            p95.push(Math.max(0, latVal));
        }
        const maxRps = 260;
        const maxLat = 130;

        function draw() {
            const t = performance.now() * 0.001;
            g.clearRect(0, 0, w, h);

            // Background
            g.fillStyle = p.bg;
            g.fillRect(0, 0, w, h);

            // Grid
            g.strokeStyle = `rgba(${p.axis}, 0.06)`;
            g.lineWidth = 0.4;
            for (let i = 1; i <= 4; i++) {
                const gy = pad.top + (i / 4) * plotH;
                g.beginPath(); g.moveTo(pad.left, gy); g.lineTo(w - pad.right, gy); g.stroke();
            }
            for (let i = 1; i <= 4; i++) {
                const gx = pad.left + (i / 4) * plotW;
                g.beginPath(); g.moveTo(gx, pad.top); g.lineTo(gx, h - pad.bottom); g.stroke();
            }

            // Axes
            g.strokeStyle = `rgba(${p.axis}, 0.2)`;
            g.lineWidth = 0.8;
            g.beginPath();
            g.moveTo(pad.left, h - pad.bottom); g.lineTo(w - pad.right, h - pad.bottom);
            g.moveTo(pad.left, h - pad.bottom); g.lineTo(pad.left, pad.top);
            g.stroke();

            // Animated sweep
            const cycle = (t % 8) / 8;
            const progress = cycle < 0.12 ? 0 : Math.min(1, (cycle - 0.12) / 0.72);
            const maxPt = progress * (pts - 1);

            // Y-axis labels (dual axis: left = RPS, right = Latency ms)
            g.fillStyle = `rgba(${p.axis}, 0.45)`;
            g.font = '8px Inter, sans-serif';
            g.textAlign = 'right';
            g.textBaseline = 'middle';
            for (let i = 0; i <= 4; i++) {
                const val = Math.round(maxRps * (1 - i / 4));
                const y = pad.top + (i / 4) * plotH;
                g.fillText(val, pad.left - 6, y);
            }
            // Right axis (ms)
            g.textAlign = 'left';
            for (let i = 0; i <= 4; i++) {
                const val = Math.round(maxLat * (1 - i / 4));
                const y = pad.top + (i / 4) * plotH;
                g.fillText(val + 'ms', w - pad.right + 6, y);
            }

            // X-axis label
            g.fillStyle = `rgba(${p.axis}, 0.5)`;
            g.font = '9px Inter, sans-serif';
            g.textAlign = 'center';
            g.fillText('Test Duration', w / 2, h - 6);

            // Y-axis labels
            g.fillStyle = `rgba(${p.axis}, 0.45)`;
            g.font = '8px Inter, sans-serif';
            g.textAlign = 'center';
            g.fillText('RPS', 14, h / 2 - 30);
            g.fillText('Latency', w - 12, h / 2 + 30);

            // ---- p95 Latency (drawn first, behind) ----
            g.beginPath();
            g.strokeStyle = `rgba(${p.s3}, 0.65)`;
            g.lineWidth = 1.4;
            let p95End = null;
            for (let i = 0; i <= maxPt && i < pts; i++) {
                const px = pad.left + (i / (pts - 1)) * plotW;
                const py = pad.top + plotH * (1 - p95[i] / maxLat);
                i === 0 ? g.moveTo(px, py) : g.lineTo(px, py);
                p95End = { px, py, i };
            }
            g.stroke();
            if (p95End) {
                g.fillStyle = `rgba(${p.s3}, 0.8)`;
                g.beginPath(); g.arc(p95End.px, p95End.py, 3, 0, Math.PI * 2); g.fill();
            }

            // ---- Requests per Second ----
            g.beginPath();
            g.strokeStyle = `rgba(${p.s2}, 0.75)`;
            g.lineWidth = 1.8;
            let rpsEnd = null;
            for (let i = 0; i <= maxPt && i < pts; i++) {
                const px = pad.left + (i / (pts - 1)) * plotW;
                const py = pad.top + plotH * (1 - rps[i] / maxRps);
                i === 0 ? g.moveTo(px, py) : g.lineTo(px, py);
                rpsEnd = { px, py, i };
            }
            g.stroke();
            if (rpsEnd) {
                g.fillStyle = `rgba(${p.s2}, 0.9)`;
                g.beginPath(); g.arc(rpsEnd.px, rpsEnd.py, 4, 0, Math.PI * 2); g.fill();
            }

            // ---- Virtual Users (area fill) ----
            if (maxPt > 0) {
                g.beginPath();
                const firstVUX = pad.left;
                const firstVUY = pad.top + plotH * (1 - vus[0] / 50);
                g.moveTo(firstVUX, firstVUY);
                for (let i = 1; i <= maxPt && i < pts; i++) {
                    const px = pad.left + (i / (pts - 1)) * plotW;
                    const py = pad.top + plotH * (1 - vus[i] / 50);
                    g.lineTo(px, py);
                }
                g.lineTo(pad.left + (maxPt / (pts - 1)) * plotW, h - pad.bottom);
                g.lineTo(firstVUX, h - pad.bottom);
                g.closePath();
                const vuGrad = g.createLinearGradient(0, pad.top, 0, h - pad.bottom);
                vuGrad.addColorStop(0, `rgba(${p.s1}, 0.18)`);
                vuGrad.addColorStop(1, `rgba(${p.s1}, 0.02)`);
                g.fillStyle = vuGrad;
                g.fill();
            }

            // Virtual Users line
            g.beginPath();
            g.strokeStyle = `rgba(${p.s1}, 0.55)`;
            g.lineWidth = 1.2;
            g.setLineDash([4, 3]);
            let vuEnd = null;
            for (let i = 0; i <= maxPt && i < pts; i++) {
                const px = pad.left + (i / (pts - 1)) * plotW;
                const py = pad.top + plotH * (1 - vus[i] / 50);
                i === 0 ? g.moveTo(px, py) : g.lineTo(px, py);
                vuEnd = { px, py, i };
            }
            g.stroke();
            g.setLineDash([]);
            if (vuEnd) {
                g.fillStyle = `rgba(${p.s1}, 0.75)`;
                g.beginPath(); g.arc(vuEnd.px, vuEnd.py, 3.5, 0, Math.PI * 2); g.fill();
            }

            // Legend
            const lx = pad.left + 8;
            const ly = pad.top - 16;
            // RPS
            g.fillStyle = `rgba(${p.s2}, 0.8)`; g.fillRect(lx, ly, 12, 3);
            g.fillStyle = `rgba(${p.axis}, 0.6)`; g.font = '8px Inter, sans-serif'; g.textAlign = 'left';
            g.fillText('RPS', lx + 15, ly + 2);
            // VUs
            g.fillStyle = `rgba(${p.s1}, 0.6)`; g.fillRect(lx + 44, ly, 12, 3);
            g.fillStyle = `rgba(${p.axis}, 0.6)`;
            g.fillText('VUs', lx + 59, ly + 2);
            // p95
            g.fillStyle = `rgba(${p.s3}, 0.7)`; g.fillRect(lx + 88, ly, 12, 3);
            g.fillStyle = `rgba(${p.axis}, 0.6)`;
            g.fillText('p95', lx + 103, ly + 2);

            animFrame = requestAnimationFrame(draw);
        }

        animFrame = requestAnimationFrame(draw);

        return () => {
            offThemeChange();
            if (animFrame) cancelAnimationFrame(animFrame);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                width: '100%',
                marginTop: '16px',
                borderRadius: '8px',
                border: '1px solid var(--plot-border)',
                display: 'block',
                background: 'var(--plot-bg)',
            }}
            aria-label="Stress test performance metrics: Virtual Users, Requests per Second, and p95 Latency"
        />
    );
}

export default PerformanceMetricsPlot;